import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LuMail, LuMessageCircle, LuSend, LuExternalLink, LuMessageSquare, LuArrowLeft } from "react-icons/lu";
import { Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { replyToConversation } from '../../redux/reducers/communicationSlice';

const ConversationThread = ({ onBack, isMobile }) => {
  const dispatch = useDispatch();
  const { currentConversation, sendingMessage } = useSelector((state) => state.communication);
  const [replyText, setReplyText] = useState('');

  if (!currentConversation) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
        <LuMessageSquare size={48} className="mb-3 opacity-25" />
        <div className="fw-semibold mb-1">Select a conversation</div>
        <div className="small">or start a new one</div>
      </div>
    );
  }

  const handleReply = () => {
    if (!replyText.trim()) return;
    dispatch(replyToConversation({ id: currentConversation._id, content: replyText }))
      .then(() => setReplyText(''));
  };

  const customer = currentConversation.customerId || {};
  const phoneObj = customer.communicationPreferences?.whatsappNumber?.number 
    ? customer.communicationPreferences.whatsappNumber 
    : (typeof customer.phone === 'object' ? customer.phone : { countryCode: '+1', number: customer.phone || '' });

  const number = `${phoneObj.countryCode || ''}${phoneObj.number || ''}`.replace(/\D/g, '');
  const waLink = `https://wa.me/${number}`;

  return (
    <div className="d-flex flex-column h-100">
      {/* Header */}
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          {isMobile && (
            <Button variant="link" className="p-0 text-dark me-2" onClick={onBack}>
              <LuArrowLeft size={20} />
            </Button>
          )}
          <div>
            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
              {currentConversation.channel === 'email' ? <LuMail size={16} /> : <LuMessageCircle size={16} />}
              {customer.name || 'Unknown Customer'}
            </h6>
            <div className="small text-muted">{customer.email || 'No email'}</div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <span className={`badge rounded-pill px-3 py-1 fw-semibold`} 
                style={{ fontSize: '11px', background: 'rgba(40,176,196,0.1)', color: '#28B0C4' }}>
            {currentConversation.status}
          </span>
        </div>
      </div>

      <div className="p-3 border-bottom bg-light">
        <div className="fw-semibold small">{currentConversation.subject || 'WhatsApp Chat'}</div>
      </div>

      {/* Messages */}
      <div className="flex-grow-1 p-3 overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
        {currentConversation.channel === 'whatsapp' && (
          <div className="d-flex align-items-center gap-2 rounded-3 p-3 mb-3"
            style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0' }}>
            <LuMessageCircle size={16} style={{ color: '#10b981', flexShrink: 0 }} />
            <span className="small fw-semibold" style={{ color: '#10b981' }}>
              WhatsApp conversation — messages tracked for reference
            </span>
            <a href={waLink} target="_blank" rel="noreferrer"
              className="btn btn-sm rounded-3 ms-auto fw-semibold"
              style={{ fontSize: '12px', background: '#10b981', color: 'white', border: 'none' }}>
              <LuExternalLink size={12} /> Open WhatsApp
            </a>
          </div>
        )}

        {currentConversation.messages.map((message, idx) => (
          message.direction === 'outbound' ? (
            <div key={idx} className="d-flex justify-content-end mb-3">
              <div style={{
                maxWidth: '75%',
                background: 'rgba(40,176,196,0.1)',
                borderRadius: '12px 12px 0 12px',
                padding: '10px 14px',
                borderRight: '3px solid #28B0C4'
              }}>
                <div style={{ fontSize: '13px', color: '#1e293b' }}>{message.content}</div>
                <div className="text-end mt-1" style={{ fontSize: '10px', color: '#94a3b8' }}>
                  {moment(message.sentAt).format('MMM D [at] HH:mm')}
                  {message.status === 'read' ? ' ✓✓' : ' ✓'}
                </div>
              </div>
            </div>
          ) : (
            <div key={idx} className="d-flex justify-content-start mb-3">
              <div style={{
                maxWidth: '75%',
                background: 'white',
                borderRadius: '12px 12px 12px 0',
                padding: '10px 14px',
                border: '1.5px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '11px', color: '#28B0C4', fontWeight: 600, marginBottom: 4 }}>
                  {message.sentByName || customer.name}
                </div>
                <div style={{ fontSize: '13px', color: '#1e293b' }}>{message.content}</div>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: 4 }}>
                  {moment(message.sentAt).format('MMM D [at] HH:mm')}
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Reply Area */}
      {currentConversation.channel === 'email' ? (
        <div className="border-top p-3">
          <textarea
            className="form-control"
            placeholder="Type your reply..."
            rows={3}
            style={{ borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', resize: 'none' }}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="text-muted small">Reply will be sent to {customer.email}</span>
            <Button className="rounded-3 fw-semibold d-flex align-items-center gap-2"
              style={{ fontSize: '13px', background: '#28B0C4', border: 'none', color: 'white' }}
              disabled={!replyText.trim() || sendingMessage}
              onClick={handleReply}>
              {sendingMessage ? <Spinner size="sm" /> : <LuSend size={14} />}
              Send Reply
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-top p-3 text-center">
          <p className="text-muted small mb-2">Continue this conversation in WhatsApp</p>
          <a href={waLink} target="_blank" rel="noreferrer"
            className="btn rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2 mx-auto"
            style={{ background: '#10b981', color: 'white', border: 'none', maxWidth: '200px', fontSize: '13px' }}>
            <LuMessageCircle size={14} /> Open WhatsApp
          </a>
        </div>
      )}
    </div>
  );
};

export default ConversationThread;
